import { LitElement, html } from "lit-element";
import WebMidi from "webmidi";
import colorConvert, { rgb } from "color-convert";

import { scale, getRandomInRange, getRandomRGBColor } from "./utils";
const TERP = require("../scripts/TERP.js");

const CC_MAX = 127;

class PitchRadialGradient extends LitElement {
    static get properties() {
        return {
            backgroundColor: { type: String },
            noteColor: { type: String },
            width: { type: String },
            height: { type: String },
            widthDivision: { type: Number },
            heightDivision: { type: Number },
            left: { type: String },
            top: { type: String },
            bottom: { type: String },
            right: { type: String },
            position: { type: String },
            opacity: { type: Number },
            lowNote: { type: Number },
            highNote: { type: Number },
            colorSystem: { type: String },
            colorValues: { type: Array },
            mixBlendMode: { type: String },
        };
    }

    constructor() {
        super();

        this.container = null;
        this.noteColor = null;
        this.width = null;
        this.height = null;
        this.widthDivision = 1;
        this.heightDivision = 1;
        this.left = "0%";
        this.top = "0%";
        this.bottom = null;
        this.colorOptions = {};
        this.offset = 0;
        this.lowNote = 21;
        this.highNote = 108;
        this.colorValues = [];
        this.mixBlendMode = "normal";
        this.position = "relative";
        this.backgroundColor = "rgb(0, 0, 0";
    }

    handlePitch(e) {
        if (e.note.number >= this.lowNote && e.note.number <= this.highNote) {
            this.container = this.shadowRoot.querySelector("#container");
            const element = document.createElement("div");
            element.id = `note${e.note.number}`;
            // write general style function
            const { note } = e;
            const scaledVelocity = TERP.scale(e.note.velocity, 0, 1, 0, 127);
            note.velocity = scaledVelocity;
            this.note = note;
            element.style = this.getNoteStyle(note);

            this.container.appendChild(element);

            setTimeout(() => {
                this.container.removeChild(element);
            }, 100);
        }
    }

    // handleNoteOff(e) {
    //     if (e.note.number >= this.lowNote && e.note.number <= this.highNote) {
    //         const element = this.shadowRoot.querySelector(
    //             `#note${e.note.number}`
    //         );

    //         if (element) {
    //             //console.log('element removed');
    //             for (let i = 0; i < this.activeNotes.length; i++) {
    //                 if (this.activeNotes[i].id === element.id) {
    //                     this.activeNotes.splice(i, 1);
    //                     break;
    //                 }
    //             }
    //             this.container.removeChild(element);
    //         } else {
    //             console.log("no element to remove");
    //         }
    //     }
    // }

    getResponsiveColor(note) {
        const responsiveColor = this.colorValues.map((val) => {
            if (val === -1) {
                if (this.colorSystem === "cmyk") {
                    return Math.floor(scale(note.velocity, 0, 127, 30, 100));
                }
                if (this.colorSystem === "lab") {
                    return Math.floor(scale(note.velocity, 0, 127, 30, 100));
                }
                if (this.colorSystem === "rgb") {
                    return Math.floor(scale(note.velocity, 0, 127, 150, 255));
                }
            } else {
                return val;
            }
        });
        return responsiveColor;
    }

    getRGBColor(responsiveColor) {
        const system = this.colorSystem;
        let rgbColor = "";

        if (system === "rgb") {
            rgbColor = responsiveColor;
        }

        if (system === "cmyk") {
            rgbColor = colorConvert.cmyk.rgb(responsiveColor);
        }
        if (system === "lab") {
            rgbColor = colorConvert.lab.rgb(responsiveColor);
        }
        return rgbColor;
    }

    getNoteColor(note) {
        if (!note) {
            return;
        }
        let color = "";
        let rgb = "";
        const responsiveColor = this.getResponsiveColor(note);

        rgb = this.getRGBColor(responsiveColor);
        const a = scale(this.lightness, 0, 100, 0, 1);
        color = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${1})`;
        return color;
    }

    updateNoteColors() {
        const noteSize = TERP.scale(
            this.note.number,
            this.lowNote,
            this.highNote,
            0,
            100
        );

        const containerHeight = window
            .getComputedStyle(this.container)
            .height.slice(0, -2);
        const containerWidth = window
            .getComputedStyle(this.container)
            .width.slice(0, -2);
        this.activeNotes.forEach((element) => {
            // const note = { number: element.id.slice(4, element.id.length) };
            element.style = `
      display: inline-block;
      position: absolute;
      width: ${containerWidth};
      height: ${containerHeight};
      background: radial-gradient(${
          this.noteColor ? this.noteColor : this.getNoteColor(this.note)
      } 2%, transparent ${noteSize}%);
      transition: opacity .5s ease-out;
      opacity: 0;
      `;
        });
    }

    getNoteStyle(note) {
        const noteSize = TERP.scale(
            note.number,
            this.lowNote,
            this.highNote,
            0,
            100
        );
        let style = ``;
        const containerHeight = window
            .getComputedStyle(this.container)
            .height.slice(0, -2);
        const containerWidth = window
            .getComputedStyle(this.container)
            .width.slice(0, -2);

        style = `
    display: inline-block;
    position: absolute;
    width: ${containerWidth};
    height: ${containerHeight};
    background: radial-gradient(${
        this.noteColor ? this.noteColor : this.getNoteColor(note)
    } 2%, transparent ${noteSize}%);
    `;

        return style;
    }

    render() {
        if (this.container) {
            this.updateNoteColors();
        }

        if (WebMidi.supported) {
            return html`
                <style>
                    :host {
                      margin: 0 auto;
                    }
                    #container {
                      position: ${this.position};
                      display: inline-block;
                      width: ${this.width
                        ? this.width
                        : `calc(100% / ${this.widthDivision})`};

                      height: ${this.height
                        ? this.height
                        : `calc(100% / ${this.heightDivision})`};

                      left: ${this.left};
                      top: ${this.top};
                      background: ${this.ccResponsive
                        ? ccColor
                        : this.backgroundColor};
                      mixblendmode: ${this.mixBlendMode};
                      opacity: ${this.opacity};
                </style>
                <div id="container"></div>
            `;
        } else {
            return html``;
        }
    }
}

customElements.define("pitch-radial-gradient", PitchRadialGradient);

// const r = TERP.scale(this.knobOneValue, 0, CC_MAX, 0, 255);
// const g = TERP.scale(this.knobTwoValue, 0, CC_MAX, 0, 255);
// const b = TERP.scale(this.knobThreeValue, 0, CC_MAX, 0, 255);
// this.lightness = colorConvert.rgb.lab([r, g, b])[0];
// const ccColor = `rgb(${r}, ${g}, ${b})`;
// this.updateNoteColors();
